import { useFrame } from '@react-three/fiber';
import { useSolubilityStore } from '../store/useSolubilityStore';
import LabRoom from '../../lab/shared/LabRoom';
import { Beaker } from '../../lab/shared/Beaker';
import { TripodStand } from '../../lab/shared/TripodStand';
import { WireGauze } from '../../lab/shared/WireGauze';
import { BunsenBurner } from '../../lab/shared/BunsenBurner';
import { Thermometer } from '../../lab/shared/Thermometer';
import { ElectronicScale } from '../../lab/shared/ElectronicScale';
import { GlassRod } from '../../lab/shared/GlassRod';
import { SaltParticles } from './SaltParticles';
import { PouringCylinder, CylinderWaterStream, SaltTransferAnimation } from './PourStreams';

export function SolubilityScene3D() {
  const labStage = useSolubilityStore(s => s.labStage);
  const waterVolumeMl = useSolubilityStore(s => s.waterVolumeMl);
  const waterTempC = useSolubilityStore(s => s.waterTempC);
  const saltOnScaleG = useSolubilityStore(s => s.saltOnScaleG);
  const saltInBeakerG = useSolubilityStore(s => s.saltInBeakerG);
  const dissolvedFraction = useSolubilityStore(s => s.dissolvedFraction);
  const isHeating = useSolubilityStore(s => s.isHeating);
  const heatIntensity = useSolubilityStore(s => s.heatIntensity);
  const isStirring = useSolubilityStore(s => s.isStirring);
  const isPouringWater = useSolubilityStore(s => s.isPouringWater);
  const pourProgress = useSolubilityStore(s => s.pourProgress);
  const isTransferringSalt = useSolubilityStore(s => s.isTransferringSalt);
  const transferProgress = useSolubilityStore(s => s.transferProgress);
  const tick = useSolubilityStore(s => s.tick);
  const toggleHeating = useSolubilityStore(s => s.toggleHeating);
  const setHeatIntensity = useSolubilityStore(s => s.setHeatIntensity);
  const simulationSpeed = useSolubilityStore(s => s.simulationSpeed);

  useFrame((_, delta) => {
    tick(delta * simulationSpeed);
  });

  const beakerWaterLevel = waterVolumeMl > 0 ? waterVolumeMl / 150 : 0;
  const liquidColor = dissolvedFraction > 0.5 ? '#f6fbff' : '#ffffff';

  // Beaker world frame (group is at y=-0.585, then +1.5*0.6 + 0.01 for wire-gauze height)
  const beakerBaseY = -0.585 + 1.5 * 0.6 + 0.01;
  const beakerScale = 1.5;
  // Beaker geometry: liquid bottom is at -beakerHeight/2 + 0.006 in local; with scale 1.5 and Beaker's
  // internal offset of beakerHeight/2, the world Y of the liquid bottom is approximately:
  const liquidBottomWorldY = beakerBaseY + (0.006 * beakerScale);

  return (
    <group>
      <LabRoom />

      <group position={[0, -0.585, -0.2]}>
        <TripodStand position={[0, 0, 0]} scale={0.6} />
        <WireGauze position={[0, 1.5 * 0.6, 0]} scale={0.6} />

        <Beaker
          position={[0, 1.5 * 0.6 + 0.01, 0]}
          scale={beakerScale}
          waterLevel={beakerWaterLevel}
          waterTemp={waterTempC}
          liquidColor={liquidColor}
        />

        <BunsenBurner
          position={[0, 0, 0]}
          scale={4}
          flameState={isHeating ? 'heating' : 'off'}
          intensity={heatIntensity}
          onToggle={toggleHeating}
          onAdjust={() => setHeatIntensity(heatIntensity >= 1.0 ? 0.2 : heatIntensity + 0.4)}
        />

        <Thermometer
          position={[0.06, 1.5, 0]}
          temperature={waterTempC}
          isStirring={isStirring}
        />

        {(saltInBeakerG > 0) && (
          <SaltParticles
            position={[0, liquidBottomWorldY - (-0.585), 0]}
            beakerInnerRadius={0.17 * beakerScale - 0.012}
            beakerBottomY={0}
            totalSaltG={saltInBeakerG}
            dissolvedFraction={dissolvedFraction}
            isStirring={isStirring}
          />
        )}

        {(saltInBeakerG > 0 || labStage === 'stirring') && (
          <GlassRod
            position={[0.05, 1.5 * 0.6 + 0.55, 0.0]}
            isStirring={isStirring}
            stirCenter={[0, 1.5 * 0.6 + 0.4, 0]}
            stirRadius={0.07}
          />
        )}
      </group>

      <group position={[1.0, -0.585, 0.2]}>
        <ElectronicScale
          position={[0, 0.04, 0]}
          rotation={[0, -Math.PI / 6, 0]}
          readingG={saltOnScaleG}
          showSaltPile={saltOnScaleG > 0 && !isTransferringSalt}
        />
      </group>

      {/* Animated measuring cylinder — lifts off the workbench and tilts over the
          beaker while pouring, then returns. Replaces the old static cylinder. */}
      <PouringCylinder
        isPouring={isPouringWater}
        pourProgress={pourProgress}
        showAtRest={labStage === 'setup' && !isPouringWater}
        liquidLevelMlAtRest={50}
        liquidColor="#f0f9ff"
      />

      {/* Visible water column from the tilted cylinder spout into the beaker */}
      <CylinderWaterStream isPouring={isPouringWater} pourProgress={pourProgress} />

      {/* Salt-transfer animation — weighing dish lifts off the scale, tilts, pours into the beaker */}
      <SaltTransferAnimation active={isTransferringSalt} progress={transferProgress} />
    </group>
  );
}

export default SolubilityScene3D;
